import { Typography } from "@/components";
import useCalendarItems from "@/hooks/useCalendarItems";
import type {
	CalendarItem,
	DateGroupedItem,
	EventItem,
	ReminderItem,
} from "@/hooks/useCalendarItems";
import { cn } from "@/libraries/cn";
import { parseDate } from "@/libraries/dayjs-relative";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import { Link } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { Dayjs } from "dayjs";
import { useEffect, useLayoutEffect, useRef } from "react";

interface ListDateProps {
	date: Dayjs;
}

const ListDate = ({ date }: ListDateProps) => {
	return (
		<div className="flex flex-col justify-center items-start">
			<Typography fontWeight={400} fontSize={24} className="text-gray-200">
				{date.format("DD")}
			</Typography>
			<Typography fontWeight={400} fontSize={14} className="text-gray-200">
				{date.format("ddd")}
			</Typography>
		</div>
	);
};

const CardEvent = ({
	title,
	description,
	start_datetime,
	end_datetime,
}: EventItem) => {
	const startAt = parseDate(start_datetime);
	const endAt = parseDate(end_datetime);
	return (
		<Card
			variant="elevation"
			className="w-full !rounded-xl [&_.MuiCardContent-root]:!p-2"
		>
			<CardContent>
				<div className="flex flex-col gap-1">
					<div className="flex justify-center items-center gap-1">
						<CalendarMonthRoundedIcon fontSize="small" />
						<Typography fontWeight={400} fontSize={14} className="flex-1">
							{[startAt.format("HH:mm A"), endAt.format("HH:mm A")].join(" - ")}
						</Typography>
						<Typography
							fontWeight={700}
							fontSize={14}
							className="rounded-md py-[2px] px-1 text-primary bg-primary-light"
						>
							Event
						</Typography>
					</div>
					<Typography fontWeight={700} fontSize={16}>
						{title}
					</Typography>
					{description ? (
						<Typography fontWeight={400} fontSize={14}>
							Lorem ipsum dolor, sit amet consectetur
						</Typography>
					) : null}
				</div>
			</CardContent>
		</Card>
	);
};

const CardTodo = ({ name, repeat, notes, alarm }: ReminderItem) => {
	const alarmAt = alarm ? parseDate(alarm) : null;
	return (
		<Card
			variant="elevation"
			className="w-full !rounded-xl [&_.MuiCardContent-root]:!p-2"
		>
			<CardContent className="flex gap-2 items-start justify-center">
				<div className="flex flex-col gap-1 flex-1">
					<div className="flex gap-3 items-center">
						{alarmAt ? (
							<div className="flex gap-1 items-center">
								<NotificationsNoneRoundedIcon fontSize="small" />
								<Typography fontWeight={400} fontSize={14}>
									{alarmAt.format("HH:mm A")}
								</Typography>
							</div>
						) : null}
						{repeat?.toLowerCase() !== "never" ? (
							<div className="flex gap-1 items-center flex-1">
								<ReplayRoundedIcon fontSize="small" />
								<Typography fontWeight={400} fontSize={14}>
									{repeat}
								</Typography>
							</div>
						) : null}
					</div>
					<Typography fontWeight={700} fontSize={16}>
						{name}
					</Typography>
					{notes ? (
						<Typography fontWeight={400} fontSize={14}>
							{notes}
						</Typography>
					) : null}
				</div>
				<Typography
					fontWeight={700}
					fontSize={14}
					className="rounded-md py-[2px] px-1 text-success-200 bg-success-100"
				>
					To-Do
				</Typography>
			</CardContent>
		</Card>
	);
};

interface CardEventsProps {
	items: CalendarItem[];
}

const CardEvents = ({ items }: CardEventsProps) => {
	return (
		<div className="w-full flex flex-col gap-2 justify-center items-center">
			{items.map((item) =>
				item.type === "event" ? (
					<Link
						key={item.type + item.id}
						replace={true}
						// @ts-expect-error
						search={(s) => ({ ...s, eid: item.id })}
						className="w-full"
					>
						<CardEvent {...item} />
					</Link>
				) : (
					<Link
						key={item.type + item.id}
						replace={true}
						// @ts-expect-error
						search={(s) => ({ ...s, rid: item.id })}
						className="w-full"
					>
						<CardTodo {...item} />
					</Link>
				),
			)}
		</div>
	);
};

type ListCardProps = DateGroupedItem;

const ListCard = ({ date, items }: ListCardProps) => {
	const isEmpty = !items.length;
	return (
		<div
			className={cn("w-full flex gap-4 justify-center", {
				"items-center": isEmpty,
				"items-start": !isEmpty,
			})}
		>
			<ListDate date={date} />
			<div className="flex-1">
				{isEmpty ? <Divider /> : <CardEvents items={items} />}
			</div>
		</div>
	);
};

interface CalendarEventsProps {
	selectedDate: Dayjs;
	days: Dayjs[];
	startDate: string;
	endDate: string;
}

const CalendarEvents = ({
	selectedDate,
	days,
	startDate,
	endDate,
}: CalendarEventsProps) => {
	const hasScrolledToInitialDate = useRef(false);
	const scrollRef = useRef<HTMLDivElement>(null);
	const { items } = useCalendarItems({
		days,
		startDate,
		endDate,
	});
	const dateEventVirtualizer = useVirtualizer({
		count: items.length,
		gap: 12,
		overscan: 0,
		paddingEnd: 16,
		estimateSize: () => 100,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
	});
	const dateEventVirtualizerItems = dateEventVirtualizer.getVirtualItems();

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (selectedDate && dateEventVirtualizerItems.length) {
			const itemIdx = items.findIndex(
				(item) => item.date.date() === selectedDate.date(),
			);
			if (itemIdx >= 0)
				dateEventVirtualizer.scrollToIndex(itemIdx, {
					behavior: "auto",
					align: "start",
				});
		}
	}, [selectedDate]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useLayoutEffect(() => {
		if (
			selectedDate &&
			dateEventVirtualizerItems.length &&
			!hasScrolledToInitialDate.current
		) {
			const itemIdx = items.findIndex(
				(item) => item.date.date() === selectedDate.date(),
			);
			if (itemIdx >= 0) {
				dateEventVirtualizer.scrollToIndex(itemIdx, {
					behavior: "auto",
					align: "start",
				});
				hasScrolledToInitialDate.current = true;
			}
		}
	}, [selectedDate, dateEventVirtualizerItems.length]);

	return (
		<div ref={scrollRef} className="flex-1 mt-40 overflow-y-auto">
			<ul
				className="relative w-full"
				style={{
					height: dateEventVirtualizer.getTotalSize(),
				}}
			>
				{dateEventVirtualizerItems.map((dateEventVirtualizerItem) => {
					const item = items[dateEventVirtualizerItem.index];
					if (!item) return null;
					return (
						<li
							key={dateEventVirtualizerItem.key}
							ref={dateEventVirtualizer.measureElement}
							data-index={dateEventVirtualizerItem.index}
							className="container w-full absolute top-0 left-0"
							style={{
								transform: `translateY(${dateEventVirtualizerItem.start}px)`,
							}}
						>
							<ListCard date={item.date} items={item.items} />
						</li>
					);
				})}
			</ul>
		</div>
	);
};

export default CalendarEvents;
